import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getTicketDetailOptions } from '@mopla/ticketshop-api';

import { IMyTicket } from '../../ticketshop/types';

import { mapTicketDetailDto } from './mapTicketDetailDto';

interface IUseMyTicketDetailResult {
	ticket: IMyTicket | null;
	isLoading: boolean;
}

/**
 * Fetch a single purchased ticket for the detail page from the single-ticket
 * endpoint `GET /api/v1/me/tickets/{purchaseId}` (`TicketDetailView`) — the only
 * source that carries the full detail (ticketCode / secretWord / passengerName).
 * The caller shows a skeleton while loading and bails to home on a failed /
 * empty fetch; we deliberately do not seed from the cached `me/tickets` list.
 */
export const useMyTicketDetail = (
	purchaseId?: string
): IUseMyTicketDetailResult => {
	const { data, isLoading } = useQuery({
		...getTicketDetailOptions({ path: { purchaseId: purchaseId ?? '' } }),
		enabled: !!purchaseId,
	});

	const ticket = useMemo(
		() => (data ? mapTicketDetailDto(data) : null),
		[data]
	);

	return { ticket, isLoading };
};
